import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import MediaPicker from '../components/MediaPicker';
import { apiFetch } from '../lib/api';
import { Plus, X, Loader2, Edit2, Save, Power, Trash2, Image as ImageIcon } from 'lucide-react';

// 媒體報導後台管理（2026-10-19 Kevin 需求：每一則報導都要能喺 CMS 修改）
interface NewsRow {
  id: number;
  newsDate: string;
  title: string;
  summary: string;
  content: string;
  sourceName: string;
  sourceUrl: string;
  imageUrl: string;
  isActive: number;
}

const emptyForm = {
  newsDate: new Date().toISOString().slice(0, 10),
  title: '',
  summary: '',
  content: '',
  sourceName: '',
  sourceUrl: '',
  imageUrl: '',
  isActive: true,
};

const AdminMediaNews: React.FC = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState<NewsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchNews();
  }, [navigate]);

  const fetchNews = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/public/admin/media-news');
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || '獲取媒體報導失敗');
      setNews(json.news || []);
    } catch (e: any) {
      setError(e.message || '加載失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/admin');
    }
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (n: NewsRow) => {
    setEditingId(n.id);
    setForm({
      newsDate: n.newsDate,
      title: n.title,
      summary: n.summary || '',
      content: n.content || '',
      sourceName: n.sourceName || '',
      sourceUrl: n.sourceUrl || '',
      imageUrl: n.imageUrl || '',
      isActive: n.isActive === 1,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const flash = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 2500);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = { ...form, isActive: form.isActive ? 1 : 0 };
      const res = await apiFetch(
        editingId != null ? `/api/public/admin/media-news/${editingId}` : '/api/public/admin/media-news',
        {
          method: editingId != null ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || '儲存失敗');
      flash(editingId != null ? '報導已更新' : '報導已新增');
      closeForm();
      fetchNews();
    } catch (e: any) {
      setError(e.message || '儲存失敗');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (n: NewsRow) => {
    try {
      const res = await apiFetch(`/api/public/admin/media-news/${n.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          newsDate: n.newsDate,
          title: n.title,
          summary: n.summary,
          content: n.content,
          sourceName: n.sourceName,
          sourceUrl: n.sourceUrl,
          imageUrl: n.imageUrl,
          isActive: n.isActive === 1 ? 0 : 1,
        }),
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || '切換失敗');
      flash(n.isActive === 1 ? '已停用' : '已啟用');
      fetchNews();
    } catch (e: any) {
      setError(e.message || '切換失敗');
    }
  };

  const handleDelete = async (n: NewsRow) => {
    if (!confirm(`確定要刪除「${n.title}」嗎？刪咗就復原唔到。`)) return;
    try {
      const res = await apiFetch(`/api/public/admin/media-news/${n.id}`, { method: 'DELETE' });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || '刪除失敗');
      flash('已刪除');
      fetchNews();
    } catch (e: any) {
      setError(e.message || '刪除失敗');
    }
  };

  return (
    <AdminLayout currentPage="media-news" onLogout={handleLogout}>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">媒體報導管理</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">每一則報導嘅日期、標題、內容、來源、圖片都可以喺度改</p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition"
        >
          <Plus size={16} /> 新增報導
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
      )}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">{success}</div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-red-600" size={32} />
        </div>
      ) : news.length === 0 ? (
        <div className="text-center py-16 text-gray-400">未有報導，撳「新增報導」加第一則</div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="p-3 text-left font-medium text-gray-500 dark:text-gray-300">日期</th>
                <th className="p-3 text-left font-medium text-gray-500 dark:text-gray-300">標題</th>
                <th className="p-3 text-left font-medium text-gray-500 dark:text-gray-300">來源</th>
                <th className="p-3 text-left font-medium text-gray-500 dark:text-gray-300">圖片</th>
                <th className="p-3 text-left font-medium text-gray-500 dark:text-gray-300">狀態</th>
                <th className="p-3 text-left font-medium text-gray-500 dark:text-gray-300">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {news.map((n) => (
                <tr key={n.id} className="hover:bg-red-50 dark:hover:bg-gray-700 transition-colors">
                  <td className="p-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">{n.newsDate}</td>
                  <td className="p-3 font-bold text-gray-800 dark:text-white max-w-md truncate">{n.title}</td>
                  <td className="p-3 text-gray-600 dark:text-gray-300">{n.sourceName || '-'}</td>
                  <td className="p-3">
                    {n.imageUrl ? (
                      <img src={n.imageUrl} alt={n.title} className="w-14 h-10 object-cover rounded" />
                    ) : (
                      <span className="text-gray-400 text-xs">未配圖</span>
                    )}
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => toggleActive(n)}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition ${
                        n.isActive === 1
                          ? 'bg-green-100 text-green-700 border border-green-200'
                          : 'bg-gray-100 text-gray-700 border border-gray-200'
                      }`}
                    >
                      <Power size={12} />
                      {n.isActive === 1 ? '顯示中' : '已隱藏'}
                    </button>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <button onClick={() => openEdit(n)} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-medium">
                        <Edit2 size={12} /> 編輯
                      </button>
                      <button onClick={() => handleDelete(n)} className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 text-xs font-medium">
                        <Trash2 size={12} /> 刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 新增／編輯表單 */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
              <h2 className="font-bold text-gray-800 dark:text-white">{editingId != null ? '編輯報導' : '新增報導'}</h2>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600" aria-label="關閉">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">日期 *</label>
                  <input
                    type="date"
                    value={form.newsDate}
                    onChange={(e) => setForm({ ...form, newsDate: e.target.value })}
                    required
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">來源名稱</label>
                  <input
                    type="text"
                    value={form.sourceName}
                    onChange={(e) => setForm({ ...form, sourceName: e.target.value })}
                    placeholder="例如：Tom's Hardware"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">標題 *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">摘要（列表卡顯示）</label>
                <textarea
                  value={form.summary}
                  onChange={(e) => setForm({ ...form, summary: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">詳細內容（分頁正文；空則自動用摘要）</label>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  rows={10}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">來源網址</label>
                  <input
                    type="url"
                    value={form.sourceUrl}
                    onChange={(e) => setForm({ ...form, sourceUrl: e.target.value })}
                    placeholder="https://..."
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">圖片</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={form.imageUrl}
                      onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                      placeholder="/media/media/..."
                      className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPicker(true)}
                      className="inline-flex items-center gap-1 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 whitespace-nowrap"
                    >
                      <ImageIcon size={14} /> 媒體庫選圖
                    </button>
                  </div>
                  {form.imageUrl && (
                    <img src={form.imageUrl} alt="預覽" className="mt-2 h-24 object-cover rounded" />
                  )}
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  className="rounded"
                />
                顯示喺媒體報導頁
              </label>

              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                <button type="button" onClick={closeForm} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 text-sm">
                  取消
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium"
                >
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  儲存
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 媒體庫選圖 */}
      {showPicker && (
        <MediaPicker
          selectedUrl={form.imageUrl}
          category="media-coverage"
          onSelect={(url: string) => {
            setForm((f) => ({ ...f, imageUrl: url }));
            setShowPicker(false);
          }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </AdminLayout>
  );
};

export default AdminMediaNews;
